/**
 * As chaves de cache, e o que cada notificação invalida (H02).
 *
 * ── Por que a tradução mora num arquivo só ────────────────────────────────
 *
 * O SSE avisa `conversation:{id}`. A tela mostra a conversa, a Inbox que a
 * lista e a timeline do cliente dela. Alguém tem de saber que uma notificação
 * atinge três caches.
 *
 * Se esse conhecimento ficasse espalhado, cada tela invalidaria o que lembrasse
 * — e o defeito seria sempre o mesmo: a tela que ninguém lembrou continua
 * mostrando dado velho, sem aviso. É o que a §14.3 chama de sistema que mente,
 * e a forma mais barata dele aparecer.
 *
 * Aqui a relação é dado. Uma tela nova acrescenta uma linha; esquecer a linha é
 * visível na revisão, e o teste da tradução cobre os três tipos de recurso.
 */

import { TIPOS_DE_RECURSO_ASSINAVEL, type TipoDeRecursoAssinavel } from '@cliqcrm/contracts';

/**
 * As chaves, em árvore.
 *
 * O TanStack invalida por PREFIXO, então `['inbox']` alcança
 * `['inbox', {aba: 'minhas'}]` e todas as outras variações de filtro. É o que
 * permite invalidar "a Inbox" sem enumerar cada combinação que está montada.
 */
export const chaves = {
  sessao: () => ['sessao'] as const,
  saudeDeDependencias: () => ['saude-dependencias'] as const,

  inbox: () => ['inbox'] as const,
  /*
   * O número do sino. Chave curta e PRÓPRIA, fora de `inbox`: o sino vive no
   * menu, em toda tela, e a fila só existe na tela dela — se dependesse do
   * prefixo da fila, a contagem só se atualizaria onde a fila estivesse
   * montada, que é justamente onde o sino não é preciso.
   */
  naoLidas: () => ['nao-lidas'] as const,
  /** A lista de feedbacks do suporte (#561). */
  feedbacks: () => ['feedbacks'] as const,
  /** As caixas do filtro da lista de conversas (#463). */
  caixasDoAtendimento: () => ['caixas-do-atendimento'] as const,
  /* As notificações de quem está usando (#536): o conteúdo dos cards. */
  notificacoes: () => ['notificacoes'] as const,
  /** "Menções para mim" (#569): a lista de 30 dias, atrás do aviso. */
  mencoesParaMim: () => ['notificacoes', 'mencoes'] as const,
  /** Quem cabe no "@" da nota interna de uma conversa (#569). */
  mencionaveis: (conversaId: string) => ['mencionaveis', conversaId] as const,
  inboxFiltrada: (filtro: Record<string, unknown>) => ['inbox', filtro] as const,

  conversa: (id: string) => ['conversa', id] as const,

  /*
   * O catálogo é da REVENDA, não de uma conversa — por isso a chave não leva
   * id. Uma entrada só no cache serve todas as telas que pintam pastilha, e
   * é o que evita baixar a mesma lista uma vez por conversa aberta.
   */
  catalogoDeEtiquetas: () => ['etiquetas'] as const,

  /*
   * As mensagens prontas da revenda. Chave sem parâmetro pela mesma razão do
   * catálogo de etiquetas: a lista é da organização, e uma entrada só serve à
   * caixa do atendimento e à tela de Configurações.
   */
  mensagensProntas: () => ['mensagens-prontas'] as const,

  /*
   * A equipe da revenda. Chave sem parâmetro: a lista é do tenant inteiro, e
   * toda ação sobre uma pessoa — convidar, alterar, reprovisionar, desativar —
   * invalida a mesma entrada.
   *
   * `acessoAoAtendimento` de cada linha vem de PERGUNTAR ao motor, então esta
   * consulta custa uma volta a mais que as outras: ela não fica em cache longo,
   * e quem administra recarrega ao agir.
   */
  pessoas: () => ['pessoas'] as const,
  /*
   * A seção Permissões (#1190). O catálogo é código e não muda com a
   * organização; a lista e a pessoa invalidam juntas ao salvar, pela raiz.
   */
  catalogoDePermissoes: () => ['permissoes', 'catalogo'] as const,
  pessoasDasPermissoes: () => ['permissoes', 'pessoas'] as const,
  permissoesDaPessoa: (id: string) => ['permissoes', 'pessoas', id] as const,
  limitesDaOrganizacao: () => ['limites-da-organizacao'] as const,
  carteiraDaPessoa: (usuarioId: string) => ['pessoas', usuarioId, 'carteira'] as const,
  unidades: () => ['unidades'] as const,
  times: () => ['times'] as const,

  /* As colunas do quadro. Mudam com quem administra, e o quadro as lê. */
  etapasDoFunil: () => ['etapas-do-funil'] as const,

  /*
   * Os funis da revenda. Chave PRÓPRIA, e fora de `funil`: aquele prefixo é o
   * dos quadros, e invalidá-lo a cada arrasto de card rebuscaria também a lista
   * do seletor — que só muda quando alguém mexe nas etapas.
   *
   * Sem parâmetro porque a lista é da organização, como o catálogo de
   * etiquetas: o seletor do quadro e a tela de Configurações leem a mesma
   * entrada.
   */
  funis: () => ['funis'] as const,
  /** As rotinas do "Atualizar" de um funil (#1136): a configuração do Super Admin. */
  atualizarDoFunil: (funil: string) => ['funis', 'atualizar', funil] as const,
  roteamentoDeFunil: () => ['funis', 'roteamento'] as const,
  motivosDePerda: () => ['funis', 'motivos-de-perda'] as const,

  /*
   * As organizações em que a PESSOA entra — e não as da organização atual.
   *
   * Chave fora de tudo o que é por tenant, de propósito: ela atravessa a
   * escolha. Invalidá-la junto com dado de uma organização faria a lista do
   * menu sumir e voltar a cada ação de negócio.
   */
  organizacoesDaPessoa: () => ['entrada', 'organizacoes'] as const,

  /* Os números de WhatsApp ligados. Chave própria: quem conecta invalida só ela. */
  canaisDeWhatsApp: () => ['canais-whatsapp'] as const,
  /** Todos os canais, de qualquer tipo (29/09/2026). */
  canaisConectados: () => ['canais-conectados'] as const,
  /** Quem atende cada canal, por caixa (29/09/2026). */
  atendentesDoCanal: (inbox: string) => ['atendentes-do-canal', inbox] as const,

  /*
   * Os modelos, por CAIXA. Cada número tem os seus, porque o modelo é aprovado
   * por conta da Meta — e a revenda com dois números tem duas contas.
   */
  modelosDeMensagem: (inbox: string) => ['modelos-de-mensagem', inbox] as const,

  responsaveisDaConversa: (id: string) => ['responsaveis', id] as const,

  cliente360: (id: string) => ['cliente-360', id] as const,

  funil: () => ['funil'] as const,
  responsaveisDaOportunidade: (id: string) => ['oportunidade', id, 'responsaveis'] as const,
  /*
   * As notas de UMA oportunidade. Chave com id, ao contrário do catálogo de
   * etiquetas: cada negócio tem as suas, e uma entrada só no cache faria a
   * segunda oportunidade aberta mostrar o que foi combinado na primeira.
   */
  /** Uma oportunidade, com o card do quadro (28/09/2026). */
  oportunidade: (id: string) => ['oportunidade', id, 'card'] as const,
  notasDaOportunidade: (id: string) => ['oportunidade', id, 'notas'] as const,
  notasDoCliente: (id: string) => ['cliente', id, 'notas'] as const,
  fichaDoPaciente: (id: string) => ['cliente', id, 'ficha-do-paciente'] as const,
  /* O vínculo do cliente com o paciente do SISAC e o que o SISAC tem dele (#1024). */
  pacienteDoCliente: (id: string) => ['cliente', id, 'paciente-sisac'] as const,
  /* Os atendimentos do vinculado no SISAC, as "Últimas vezes". */
  ultimasVezesDoPaciente: (id: string) =>
    ['cliente', id, 'paciente-sisac', 'ultimas-vezes'] as const,
  /* O paciente vinculado ao cliente da oportunidade, para a aba Consulta (#1024). */
  pacienteVinculadoDaConsulta: (oportunidadeId: string, codigo: string) =>
    ['oportunidade', oportunidadeId, 'consulta', 'paciente-vinculado', codigo] as const,
  consultaDaOportunidade: (id: string) => ['oportunidade', id, 'consulta'] as const,
  /*
   * A conferência da consulta marcada no SISAC (#1187), pelo horário gravado:
   * fora de `consultaDaOportunidade`, para invalidar a consulta não a rodar de novo.
   */
  conferenciaDaConsulta: (id: string, codigoDaAgenda: string) =>
    ['conferencia-no-sisac', id, codigoDaAgenda] as const,
  /* O agendamento gravado na vaga, para o "Editar" (#1187), debaixo da consulta. */
  agendamentoDaConsulta: (id: string) => ['oportunidade', id, 'consulta', 'agendamento'] as const,
  opcoesDaConsulta: () => ['consultas', 'opcoes'] as const,
  /* O preço da consulta com um médico, no agendamento (#1100). */
  precoDaConsultaDoMedico: (medicoId: string) =>
    ['consultas', 'precos', 'medico', medicoId] as const,
  /* Os preços particulares da clínica (#1084), pelo filtro da busca. Sem filtro: todos, para invalidar. */
  precosDaClinica: (filtro?: {
    readonly tipo: string;
    readonly busca: string;
    readonly pagina: number;
  }) =>
    filtro === undefined
      ? (['consultas', 'precos'] as const)
      : (['consultas', 'precos', filtro.tipo, filtro.busca, filtro.pagina] as const),
  /* As vagas livres de um médico na agenda do SISAC (#1011), pelo id dele no CliqCRM. */
  /*
   * As vagas do médico: sem dia, a janela de 14 dias; com o dia (`AAAA-MM-DD`), só ele
   * (#1187). Invalidar sem o dia leva os dias e os meses juntos.
   */
  vagasDaConsulta: (medicoId: string, dia?: string) =>
    dia === undefined
      ? (['consultas', 'vagas', medicoId] as const)
      : (['consultas', 'vagas', medicoId, 'dia', dia] as const),
  /* Os dias com vaga de um mês (`AAAA-MM`), debaixo das vagas do médico (#1187). */
  diasComVaga: (medicoId: string, mes: string) =>
    ['consultas', 'vagas', medicoId, 'mes', mes] as const,
  /* Uma lista do formulário do agendamento (convênios, planos de um convênio, origens, locais). */
  listaDaAgenda: (lista: string, parametro: string) =>
    ['consultas', 'lista', lista, parametro] as const,
  /* O endereço de um CEP (03/11/2026), para o agendamento. Não é da organização: nada o invalida. */
  enderecoDoCep: (cep: string) => ['cep', cep] as const,
  conveniosDoPaciente: (id: string) => ['cliente', id, 'convenios'] as const,
  operadoras: () => ['operadoras'] as const,
  documentosDoPaciente: (id: string) => ['cliente', id, 'documentos-do-paciente'] as const,
  examesDoPaciente: (id: string) => ['cliente', id, 'exames'] as const,
  etapasDaClinica: () => ['etapas-de-exame'] as const,
  /** O catálogo de campos de um cadastro (#568). Sem cadastro: todos, para invalidar junto. */
  camposPersonalizados: (entidade?: string) =>
    entidade === undefined
      ? (['campos-personalizados'] as const)
      : (['campos-personalizados', entidade] as const),
  tratamentosDoPaciente: (id: string) => ['cliente', id, 'tratamentos'] as const,
  catalogoDeTratamentos: () => ['tratamentos'] as const,
  prontuarioDoPaciente: (id: string) => ['cliente', id, 'prontuario'] as const,
  historicoDaOportunidade: (id: string) => ['oportunidade', id, 'historico'] as const,
  atividadesDaOportunidade: (id: string) => ['oportunidade', id, 'atividades'] as const,
  conversasDaOportunidade: (id: string) => ['oportunidade', id, 'conversas'] as const,
  documentosDaOportunidade: (id: string) => ['oportunidade', id, 'documentos'] as const,
  boardDoFunil: (pipelineId: string) => ['funil', pipelineId] as const,
  /**
   * Os atendimentos do SISAC nos cards (#1151). Fora de `['funil']` de
   * propósito: o quadro é invalidado a cada movimento e enquanto espera a
   * projeção, e cada vez seria uma leitura no SISAC. A chave leva os cards
   * (`assinatura`): muda o quadro, muda a chave, e só aí se lê de novo.
   */
  atendimentosDoFunil: (pipelineId: string, filtro: Record<string, string>, assinatura: string) =>
    ['atendimentos-do-funil', pipelineId, filtro, assinatura] as const,
  atendimentosDaOportunidade: (id: string) => ['oportunidade', id, 'atendimentos'] as const,
  /**
   * O retrato de cada recorte da lista de clientes (26/10/2026): ao voltar a
   * uma aba, ela aparece na hora e se atualiza por baixo. Com a organização,
   * porque o cache sobrevive à troca de organização na mesma aba.
   */
  listaDeClientes: (organizacao: string, usuario: string, pedido: Record<string, string>) =>
    ['clientes', 'lista', organizacao, usuario, pedido] as const,

  tarefas: () => ['tarefas'] as const,
  tarefasFiltradas: (filtro: Record<string, unknown>) => ['tarefas', filtro] as const,
  /** A quem se pode atribuir. Fora de `tarefas` porque invalidar a lista não a muda. */
  responsaveisDeTarefa: () => ['tarefas-responsaveis'] as const,
  // DENTRO de `tarefas`: concluir uma tarefa muda a lista E o detalhe dela, e
  // uma invalidação só tem de alcançar os dois.
  tarefa: (id: string) => ['tarefas', 'uma', id] as const,

  timeline: () => ['timeline'] as const,
  timelineDe: (filtro: Record<string, unknown>) => ['timeline', filtro] as const,

  /*
   * O dashboard NÃO entra em `chavesAtingidasPor`, de propósito.
   *
   * Ele é servido de agregado materializado por job (ADR-0010), que roda de
   * quinze em quinze minutos. Invalidá-lo a cada notificação de mensagem
   * refaria a consulta dezenas de vezes por minuto para receber exatamente o
   * mesmo número — e o custo cairia no banco que o vendedor está usando para
   * atender.
   */
  dashboard: (periodo: Record<string, unknown>) => ['dashboard', periodo] as const,
  /** O ranking de vendas (#557): fora das chaves atingidas, pelo mesmo motivo do dashboard. */
  rankingDeVendas: (filtro: Record<string, unknown>) => ['ranking-de-vendas', filtro] as const,
  vendasDoRanking: (filtro: Record<string, unknown>) => ['vendas-do-ranking', filtro] as const,
  /** Os demais relatórios da área de BI, pelo mesmo motivo. */
  relatorio: (nome: string, filtro: Record<string, unknown>) =>
    ['relatorio', nome, filtro] as const,

  /*
   * A fila de identidade também fica fora do `chavesAtingidasPor`.
   *
   * Candidato nasce de evento de conversa, e o SSE notifica conversa — invalidar
   * a fila a cada mensagem a refaria dezenas de vezes por minuto para, quase
   * sempre, devolver a mesma lista. Quem decide invalida na hora; quem só olha
   * recarrega quando quiser.
   */
  // ── Administração (H13) ────────────────────────────────────────────────
  //
  // Prefixo 'admin' comum a todas: invalidar a árvore inteira depois de uma
  // alteração é o certo aqui. As quatro seções se afetam — ligar uma flag
  // muda a configuração efetiva, reprocessar muda a fila —, e invalidar de
  // menos numa tela de administração faz a pessoa duvidar do que salvou.
  configuracao: () => ['admin', 'configuracao'] as const,
  /* Fora de `admin`: é sobre a conta de quem está usando, não sobre a revenda. */
  segurancaDaConta: () => ['me', 'seguranca'] as const,
  /* Também sobre quem está usando: se ELA pediu as automações, nesta organização. */
  lugarNaListaDasAutomacoes: () => ['me', 'lista-de-espera', 'automacoes'] as const,
  /* A lista inteira, de todas as organizações — a consulta da Operação Cliqfy. */
  listaDeEsperaDasAutomacoes: () => ['operacao', 'lista-de-espera', 'automacoes'] as const,
  /* Quem é Super Admin (ADR-0025): a mesma lista em qualquer organização. */
  superAdmins: () => ['operacao', 'super-admins'] as const,
  /* O catálogo de planos da gestão (#766): o mesmo em qualquer organização. */
  planos: () => ['operacao', 'planos'] as const,
  /** A lista da gestão: o prefixo invalida todas as buscas de uma vez. */
  organizacoesNaGestao: () => ['operacao', 'organizacoes'] as const,
  organizacoesNaGestaoFiltradas: (busca: string, situacao: string | null) =>
    ['operacao', 'organizacoes', busca, situacao ?? 'todas'] as const,
  organizacaoNaGestao: (id: string) => ['operacao', 'organizacao', id] as const,
  historicoDaOrganizacao: (id: string) => ['operacao', 'organizacao', id, 'historico'] as const,

  // ── Automações (fase 2) ────────────────────────────────────────────────
  //
  // Prefixo 'automacoes' comum: ligar, pausar ou arquivar muda a lista, os
  // números do painel e a contagem de cada situação ao mesmo tempo, e invalidar
  // a árvore inteira depois de um comando é o certo. O SSE não fala delas.
  automacoes: () => ['automacoes'] as const,
  /* `situacao` só para as arquivadas: as outras vêm juntas, e o filtro é da tela. */
  listaDeAutomacoes: (situacao: 'arquivada' | null) =>
    ['automacoes', 'lista', situacao ?? 'em-uso'] as const,
  painelDasAutomacoes: (janela: string) => ['automacoes', 'painel', janela] as const,
  modelosDeAutomacao: () => ['automacoes', 'modelos'] as const,
  blocosDoEditor: () => ['automacoes', 'blocos'] as const,
  historicoDaAutomacao: (id: string) => ['automacoes', 'historico', id] as const,
  automacao: (id: string) => ['automacoes', 'detalhe', id] as const,
  execucoesDasAutomacoes: (filtro: Record<string, string | number | undefined>) =>
    ['automacoes', 'execucoes', filtro] as const,
  automacoesEmEspera: () => ['automacoes', 'em-espera'] as const,

  // ── Listas (a fase 1 das automações) ──────────────────────────────────
  listas: () => ['listas'] as const,
  pedidosDeLista: () => ['listas', 'pedidos'] as const,
  conexoesDeDados: () => ['integracoes-de-dados', 'conexoes'] as const,
  mapaDoBanco: (id: string) => ['integracoes-de-dados', 'conexoes', id, 'mapa'] as const,

  // ── Integrações de dados (só a Cliqfy) ────────────────────────────────
  /* As operações SQL de uma finalidade: as consultas que os modelos leem. */
  operacoesDeDados: (finalidade: string) =>
    ['integracoes-de-dados', 'operacoes', finalidade] as const,
  /* Todas as operações, de qualquer finalidade: a tela de Operações SQL. */
  todasAsOperacoesDeDados: () => ['integracoes-de-dados', 'operacoes', 'todas'] as const,
  /* O mapa da agenda da clínica: parte → operação (ADR-0028). Só a Cliqfy lê. */
  agendaDaClinica: () => ['integracoes-de-dados', 'agenda-da-clinica'] as const,
  /* Uma lista da clínica para os padrões (#1187); os planos, por convênio. */
  listaDaAgendaDaClinica: (lista: string, convenio: string) =>
    ['integracoes-de-dados', 'agenda-da-clinica', 'lista', lista, convenio] as const,
  operacaoDeDados: (id: string) => ['integracoes-de-dados', 'operacao', id] as const,
  historicoDaOperacaoDeDados: (id: string) =>
    ['integracoes-de-dados', 'operacao', id, 'historico'] as const,
  flags: () => ['admin', 'flags'] as const,
  integracoes: () => ['admin', 'integracoes'] as const,
  /* O catálogo de plataformas é da REVENDA, e não de uma tela: quem
     pergunta "tenho portal ligado?" é a Administração hoje e o Funil
     amanhã. Chave própria, sem filtro, para os dois lerem a mesma coisa. */
  catalogoDePlataformas: () => ['admin', 'catalogo-de-plataformas'] as const,
  dlq: (filtro: Record<string, unknown>) => ['admin', 'dlq', filtro] as const,
  itemDaDlq: (id: string) => ['admin', 'dlq', 'item', id] as const,
  administracao: () => ['admin'] as const,

  candidatosDeMerge: (filtro: Record<string, unknown>) => ['identidade', 'fila', filtro] as const,
  candidatoDeMerge: (id: string) => ['identidade', 'candidato', id] as const,
  metricaDeIdentidade: () => ['identidade', 'metrica'] as const,
} as const;

export interface RecursoAssinado {
  readonly tipo: TipoDeRecursoAssinavel;
  readonly id: string;
}

/**
 * O que precisa ser buscado de novo quando a conversa PASSA DE MÃO.
 *
 * A conversa e a fila mostram o responsável, e a lista de responsáveis é de
 * onde o campo "Agente atribuído" tira quem é o atual. A lista ficava de fora:
 * depois de passar a conversa, o campo voltava a mostrar a pessoa anterior até
 * alguém recarregar a página.
 *
 * A lista lê quem atende direto no motor de conversa, que já mudou quando a
 * passagem responde. Por isso buscá-la na hora traz a pessoa nova, sem esperar
 * a projeção.
 */
export function chavesDaPassagemDeConversa(id: string): readonly (readonly unknown[])[] {
  return [chaves.conversa(id), chaves.inbox(), chaves.responsaveisDaConversa(id)];
}

/** `conversation:abc` → `{tipo, id}`. Devolve `null` no que não reconhece. */
export function interpretarRecurso(assinatura: string): RecursoAssinado | null {
  const corte = assinatura.indexOf(':');
  if (corte <= 0) return null;

  const tipo = assinatura.slice(0, corte);
  const id = assinatura.slice(corte + 1);
  if (!id) return null;

  return (TIPOS_DE_RECURSO_ASSINAVEL as readonly string[]).includes(tipo)
    ? { tipo: tipo as TipoDeRecursoAssinavel, id }
    : null;
}

/**
 * O que rebuscar quando um recurso muda.
 *
 * Prefixos, não chaves exatas — ver a nota sobre invalidação por prefixo.
 */
/**
 * As consultas de qualquer oportunidade e o paciente vinculado delas (#1024):
 * vincular ou desvincular um contato muda o `paciente_vinculado` de todas as
 * oportunidades dele, e a tela não sabe quais são.
 */
export function ehDaConsultaDeOportunidade(chave: readonly unknown[]): boolean {
  return chave[0] === 'oportunidade' && chave[2] === 'consulta';
}

export function chavesAtingidasPor(assinatura: string): readonly (readonly unknown[])[] {
  const recurso = interpretarRecurso(assinatura);
  if (!recurso) return [];

  switch (recurso.tipo) {
    case 'conversation':
      // A conversa em si, a Inbox — que mostra a prévia e o não lido — e a
      // timeline, onde a mensagem também aparece. O 360 do cliente não entra:
      // ele é caro e a conversa dentro dele já vem da mesma projeção que a
      // Inbox, então a próxima abertura já traz o novo.
      return [
        chaves.conversa(recurso.id),
        chaves.inbox(),
        // O sino: mensagem nova muda o número, e marcar como lida também.
        chaves.naoLidas(),
        chaves.timeline(),
        /*
         * Quem atende. O campo "Agente atribuído" tira o atual DESTA lista, e
         * não da conversa: sem ela aqui, a troca feita por outra pessoa não
         * aparecia até alguém recarregar a página.
         */
        chaves.responsaveisDaConversa(recurso.id),
      ];

    case 'inbox':
      // A caixa inteira mudou de composição — quem entrou, quem saiu, quem foi
      // atribuído. Nenhuma conversa específica é apontada.
      //
      // O sino entra porque a composição decide o que conta: uma conversa que
      // ganhou dono sai da conta de quem não é esse dono.
      return [chaves.inbox(), chaves.naoLidas()];

    case 'opportunity':
      // O board e a timeline, que registra a mudança de etapa (D06). O 360
      // também: a oportunidade é um dos seis blocos dele, e é dado que o
      // vendedor olha enquanto conversa.
      return [chaves.funil(), chaves.timeline(), ['cliente-360']];

    case 'notificacao':
      // Só as próprias (`notificacao:eu`): há card novo para buscar. O sino
      // não entra — a mesma mensagem já chega como `conversation:`.
      return [chaves.notificacoes()];

    case 'sessao':
      // O `/v1/me` não está no cache: quem o relê é a ligação do fluxo
      // (`provedor.tsx`). Daqui sai a administração, onde a funcionalidade
      // ligada e o que ela mostra aparecem.
      return [chaves.administracao()];

    case 'funil':
      // Uma oportunidade da organização mudou (`funil:eu`), por quem for: o
      // quadro relê. Qual, o aviso não diz; a releitura traz a carteira de quem vê.
      return [chaves.funil()];
  }
}
